import moment from "moment";
import { appendQueryString } from "utilities/queryString";
import { type FilterValues } from "./FilterContext";
import { type AddressFilterType } from "./Filters/FilterTypes/AddressFilterType";
import { type AirportSizeFilterType } from "./Filters/FilterTypes/AirportSizeFilterType";
import { type AppointmentPhaseFilterType } from "./Filters/FilterTypes/AppointmentPhaseFilterType";
import { type AppointmentTypeFilterType } from "./Filters/FilterTypes/AppointmentTypeFilterType";
import { type ArtistFilterType } from "./Filters/FilterTypes/ArtistFilterType";
import { type ArtistProfileFilterType, getOption as getArtistProfileOption } from "./Filters/FilterTypes/ArtistProfileFilterType";
import { type BookkeepingFilterType } from "./Filters/FilterTypes/BookkeepingFilterType";
import { type ClientFilterType } from "./Filters/FilterTypes/ClientFilterType";
import { type CompanyFilterType } from "./Filters/FilterTypes/CompanyFilterType";
import { type ContactFilterType } from "./Filters/FilterTypes/ContactFilterType";
import { type CostCenterFilterType } from "./Filters/FilterTypes/CostCenterFilterType";
import { type CultureFilterType } from "./Filters/FilterTypes/CultureFilterType";
import { type DocumentProgressFilterType, getOption as getDocumentProgressOption } from "./Filters/FilterTypes/DocumentProgressFilterType";
import { type EnvironmentFilterType, EnvironmentOptions } from "./Filters/FilterTypes/EnvironmentFilterType";
import { type ExtractorSessionLifeCycleFilterType } from "./Filters/FilterTypes/ExtractorSessionLifeCycleFilterType";
import { type FavoriteArtistFilterType } from "./Filters/FilterTypes/FavoriteArtistFilterType";
import { type FinancialRiskFilterType } from "./Filters/FilterTypes/FinancialRiskFilterType";
import { type FreefieldProfileFilterType } from "./Filters/FilterTypes/FreefieldProfileFilterType";
import { type FunctionGroupFilterType } from "./Filters/FilterTypes/FunctionGroupFilterType";
import { type HotelFilterType } from "./Filters/FilterTypes/HotelFilterType";
import { type InvoiceProgressFilterType, getOption as getInvoiceProgressOption } from "./Filters/FilterTypes/InvoiceProgressFilterType";
import { type KeywordFilterType } from "./Filters/FilterTypes/KeywordFilterType";
import { type MonthFilterType } from "./Filters/FilterTypes/MonthFilterType";
import { type OnlyMySelectionFilterType } from "./Filters/FilterTypes/OnlyMySelectionFilterType";
import { type OwnersFilterType } from "./Filters/FilterTypes/OwnerFilterType";
import { type PaymentMethodFilterType } from "./Filters/FilterTypes/PaymentMethodFilterType";
import { type PeriodFilterType } from "./Filters/FilterTypes/PeriodFilterType";
import { type ProductGroupFilterType } from "./Filters/FilterTypes/ProductGroupFilterType";
import { type ProductionFilterType } from "./Filters/FilterTypes/ProductionFilterType";
import { type PromoterFilterType } from "./Filters/FilterTypes/PromoterFilterType";
import { type PublicationFilterType } from "./Filters/FilterTypes/PublicationFilterType";
import { type QuarterFilterType } from "./Filters/FilterTypes/QuarterFilterType";
import { type StatusFilterType } from "./Filters/FilterTypes/StatusFilterType";
import { type TagFilterType } from "./Filters/FilterTypes/TagFilterType";
import { type TaskManagerPeriodFilterType, DefaultTaskManagerPeriodFilter } from "./Filters/FilterTypes/TaskManagerPeriodFilterType";
import { type UserToClientLifecycleFilterType, getOption, } from "./Filters/FilterTypes/UserToClientLifecycleFilterType";
import { type VenueCapacityFilterType } from "./Filters/FilterTypes/VenueCapacityFilterType";
import { type VenueCategoryFilterType } from "./Filters/FilterTypes/VenueCategoryFilterType";
import { type VenueFilterType } from "./Filters/FilterTypes/VenueFilterType";
import { type VisibleFilterType } from "./Filters/FilterTypes/VisibleFilterType";
import { type WeekFilterType } from "./Filters/FilterTypes/WeekFilterType";
import { type NotificationStatusFilterType } from "./Filters/FilterTypes/NotificationStatusFilterType";
import { type ClientAccessFilterType } from "./Filters/FilterTypes/ClientAccessFilterType";
import { type HubSpotLinkedFilterType } from "./Filters/FilterTypes/HubSpotLinkedFilterType";
import { type DataClearedFilterType } from "./Filters/FilterTypes/DataClearedFilterType";
import { type CountryFilterType } from "./Filters/FilterTypes/CountryFilterType";
import { GroupByPeriodFilterType } from "./Filters/FilterTypes/GroupByPeriodFilterType";
import { GuestlistAccessFilterType } from "./Filters/FilterTypes/GuestlistAccessFilterType";

export const mapResponseToAddressFilter = (address: server.dto.AddressFilterInstruction): AddressFilterType => ({
  countries: address?.countryIds?.map((id) => serverReference.countries.find((c) => c.key === id)) ?? [],
  city: address?.city,
  postalCode: address?.postalCode,
  states: address?.stateIds?.map((id) => serverReference.countries.flatMap((c) => c.states).find((s) => s.key === id)) ?? [],
  regions: address?.regionIds?.map((id) => serverReference.regions.find((r) => r.value === id)) ?? [],
  filterType: address?.type ?? "unknown"
});
export const mapResponseToAppointmentPhaseFilter = (phases: server.dto.AppointmentPhases[]): AppointmentPhaseFilterType => ({
  selectedPhases: (phases ?? []).map(p => serverReference.appointmentPhases.find(ap => ap.value === p))
});
export const mapResponseToAppointmentTypeFilter = (types: server.dto.AppointmentTypes[]): AppointmentTypeFilterType => ({
  selectedTypes: (types ?? []).map(t => serverReference.appointmentTypes.find(at => at.value === t))
});
export const mapResponseToAirportSizeFilter = (airportSize: server.dto.AirportSizeFilterOptions): AirportSizeFilterType => ({
  airportSize
});
export const mapResponseToArtistFilter = (artists: server.dto.ArtistSmall[]): ArtistFilterType => ({
  selectedArtists: artists
});
export const mapResponseToArtistProfileFilter = (profile: server.dto.IsArtistTypeFilterOptions): ArtistProfileFilterType => ({
  option: getArtistProfileOption(profile)
});
export const mapResponseToSingleBookkeepingFilter = (bookkeeping: server.dto.BookkeepingSmall): BookkeepingFilterType => ({
  selectedBookkeepings: bookkeeping ? [bookkeeping] : [],
});
export const mapResponseToBookkeepingFilter = (bookkeepings: server.dto.BookkeepingSmall[]): BookkeepingFilterType => ({
  selectedBookkeepings: bookkeepings ?? [],
});
export const mapResponseToClientFilter = (clients: server.dto.ClientSmall[]): ClientFilterType => ({
  clients
});
export const mapResponseToClientAccessFilter = (clientAccesses: server.dto.ClientAccesses[], currentFilter: ClientAccessFilterType): ClientAccessFilterType => ({
  clientAccesses: currentFilter.clientAccesses,
  selectedClientAccesses: clientAccesses?.map(access => currentFilter.clientAccesses.find(c => c.value === access)) ?? []
});
export const mapResponseToCompanyFilter = (companies: server.dto.CompanySmall[]): CompanyFilterType => ({
  selectedCompanies: companies
});
export const mapResponseToContactFilter = (contacts: server.dto.ContactSmallV2[]): ContactFilterType => ({
  selectedContacts: contacts
});
export const mapResponseToCostCenterFilter = (costCenters: server.dto.CostCenter[]): CostCenterFilterType => ({
  selectedCostCenters: costCenters
});
export const mapResponseToCultureFilter = (cultures: server.dto.LCID[]): CultureFilterType => ({
  selectedLCIDs: cultures ?? []
});
export const mapResponseToDataClearedFilter = (dataCleared: server.dto.DataClearedFilterSettings): DataClearedFilterType => ({
  dataCleared: dataCleared
});
export const mapResponseToDocumentProgressFilter = (progress: server.dto.DocumentLifeCycleFilterOptions): DocumentProgressFilterType => ({
  option: getDocumentProgressOption(progress)
});
export const mapResponseToEnvironmentsFilter = (environments: server.dto.Environments[]): EnvironmentFilterType => ({
  options: environments?.map((e) => EnvironmentOptions.find((o) => o.value === e)) ?? []
});
export const mapResponseToExtractorSessionLifeCycleFilter = (lifeCycles: server.dto.ExtractorSessionLifeCycles[]): ExtractorSessionLifeCycleFilterType => ({
  selectedLifeCycles: lifeCycles ?? []
});
export const mapResponseToFavoriteArtistFilter = (onlyFavorite: boolean): FavoriteArtistFilterType => ({
  onlyFavorite: onlyFavorite ?? false
});
export const mapResponseToFinancialRiskFilter = (financialRisks: server.dto.FinancialRiskLevels[]): FinancialRiskFilterType => ({
  selectedRiskLevels: financialRisks ?? []
});
export const mapResponseToFunctionGroupsFilter = (functionGroups: server.dto.FunctionGroups[]): FunctionGroupFilterType => ({
  selectedFunctionGroups: functionGroups ?? []
});

export const mapResponseToFreefieldProfileFilter = (profiles: server.dto.FreeFieldProfileSmall[]): FreefieldProfileFilterType => ({
  freefieldProfiles: profiles.map<server.dto.FreeFieldProfileForList>((p) => ({
    assignedUsers: [],
    environment: "unknown",
    freeFieldProfileId: p.freeFieldProfileId,
    title: p.title,
    permissions: undefined,
    freeFieldProfilePermissions: undefined,
  }))
});
export const mapResponseToGroupByPeriodFilter = (groupBy: server.dto.PeriodOptions): GroupByPeriodFilterType => ({
  groupBy,
});
export const mapResponseToGuestlistAccessTypeFilter = (accessTypes: server.dto.GuestlistList[], currentFilter: GuestlistAccessFilterType): GuestlistAccessFilterType => ({
  initialLists: currentFilter.initialLists,
  selectedLists: accessTypes?.map(at => currentFilter.initialLists.find(iat => iat.guestlistAccessTypeId === at.guestlistAccessTypeId))
    // Some access types may not be found for various reasons, so filter them out
    .filter(at => at != null) ?? [],
});
export const mapResponseToHotelFilter = (hotels: server.dto.CompanySmall[]): HotelFilterType => ({
  selectedHotels: hotels
});
export const mapResponseToHubSpotFilter = (linked: server.dto.LinkedFilterSettings): HubSpotLinkedFilterType => ({
  linked: linked
});
export const mapResponseToInvoiceProgressFilter = (progress: server.dto.InvoiceLifeCycleFilterOptions): InvoiceProgressFilterType => ({
  option: getInvoiceProgressOption(progress)
});
export const mapResponseToKeywordFilter = (keyword: string): KeywordFilterType => ({
  query: keyword ?? ""
});
export const mapResponseToMonthFilter = (month: Date): MonthFilterType => ({
  month: month ? moment.utc(month) : undefined,
});
export const mapResponseToNotificationStatusFilter = (notificationStatus: server.dto.NotificationStatusFilterSettings): NotificationStatusFilterType => ({
  selectedNotificationStatus: notificationStatus
});
export const mapResponseToOnlyMySelectionFilter = (showOnlyMySelection: boolean): OnlyMySelectionFilterType => ({
  showOnlyMySelection
});
export const mapResponseToOwnerFilter = (owners: server.dto.UserSmall[]): OwnersFilterType => ({
  selectedOwners: owners
});
export const mapResponseToQuarterFilter = (quarter: Date): QuarterFilterType => ({
  quarter: quarter ? moment.utc(quarter) : undefined,
});
export const mapResponseToPaymentMethodFilter = (paymentMethods: server.dto.PaymentMethods[]): PaymentMethodFilterType => ({
  selectedPaymentMethods: paymentMethods ?? []
});
export const mapResponseToPeriodFilter = (period: server.dto.PeriodFilterInstruction): PeriodFilterType => ({
  period: period?.period ?? "unknown",
  selectionStart: period?.selectionStart ? moment.utc(period.selectionStart) : undefined,
  selectionEnd: period?.selectionEnd ? moment.utc(period.selectionEnd) : undefined,
  target: period?.selectBySendOrDueDate
});
export const mapResponseToProductGroupFilter = (productGroups: server.dto.ProductGroup[]): ProductGroupFilterType => ({
  selectedProductGroups: productGroups
});
export const mapResponseToProductionFilter = (productions: server.dto.TourSmall[]): ProductionFilterType => ({
  selectedProductions: productions
});
export const mapResponseToPromoterFilter = (promoters: server.dto.CompanySmall[]): PromoterFilterType => ({
  selectedPromoters: promoters
});
export const mapResponseToPublicationFilter = (publication: server.dto.PublicationStatuses): PublicationFilterType => ({
  selectedPublication: publication
});
export const mapResponseToStatusFilter = (statuses: server.dto.StatusFilterInstruction, currentFilter: StatusFilterType): StatusFilterType => ({
  initialStatuses: currentFilter.initialStatuses,
  selectedStatuses: statuses?.statusIds
    .map(id => currentFilter.initialStatuses.find(s => s.statusId === id))
    // Some statuses may not be found for various reasons, so filter them out
    .filter(s => s != null) ?? [],
  filterType: statuses?.statusesWhereInOrNotIn ?? "in"
});
export const mapResponseToTagFilter = (tags: server.dto.TagFilterInstruction, currentFilter: TagFilterType): TagFilterType => ({
  selectedTags: tags?.tagIds
    .map((id) => currentFilter.initialTags.find((t) => t.tagId === id))
    // Some tags may not be found for various reasons, so filter them out
    .filter((t) => t != null) ?? [],
  initialTags: currentFilter.initialTags,
  filterType: tags?.tagsWhereInOrNotIn ?? "in"
});
export const mapResponseToTaskManagerPeriodFilter = (period: server.dto.TaskManagerPeriodFilters): TaskManagerPeriodFilterType => ({
  selectedTaskManagerPeriod: period ?? "allTasks"
});
export const mapResponseToUserToClientLifecycleFilter = (options: server.dto.UserToClientLifeCycles[]): UserToClientLifecycleFilterType => ({
  options: options?.length === 1 ? [getOption(options[0])] : []
});
export const mapResponseToVenueFilter = (venues: server.dto.VenueSmall[]): VenueFilterType => ({
  selectedVenues: venues
});
export const mapResponseToVenueCategoryFilter = (categories: server.dto.VenueCategories[]): VenueCategoryFilterType => ({
  selectedVenueCategories: categories ?? []
});
export const mapResponseToVenueCapacityFilter = (capacity: server.dto.VenueCapacityFilterInstruction): VenueCapacityFilterType => ({
  min: capacity?.min ?? 0,
  max: capacity?.max || serverReference.constants.venue.maxCapacity
});
export const mapResponseToVisibilityFilter = (visibility: server.dto.BooleanFilterSettings): VisibleFilterType => ({
  visibility
});
export const mapResponseToWeekFilter = (week: Date): WeekFilterType => ({
  week: week ? moment.utc(week) : undefined,
});

const countriesMap = new Map<server.dto.Countries, server.dto.Country>();
serverReference.countries.forEach(country => countriesMap.set(country.key, country));

export const mapResponseToCountriesFilter = (countries: server.dto.Countries[]): CountryFilterType => ({
  selectedCountries: countries?.map(c => countriesMap.get(c)) ?? []
});

export const mapAddressFilterToInstruction = (filter: AddressFilterType): server.dto.AddressFilterInstruction => ({
  city: filter.city,
  countryIds: filter.countries.map((c) => c.key),
  postalCode: filter.postalCode,
  regionIds: filter.regions.map((r) => r.value),
  stateIds: filter.states.map((s) => s.key),
  type: filter.filterType
});
export const mapAppointPhaseFilterToInstruction = (filter: AppointmentPhaseFilterType): server.dto.AppointmentPhases[] => filter.selectedPhases.map(p => p.value);
export const mapAppointmentTypeFilterToInstruction = (filter: AppointmentTypeFilterType): server.dto.AppointmentTypes[] => filter.selectedTypes.map(t => t.value);
export const mapAirportSizeFilterToInstruction = (filter: AirportSizeFilterType): server.dto.AirportSizeFilterOptions => filter.airportSize;
export const mapArtistFilterToInstruction = (filter: ArtistFilterType): number[] => filter.selectedArtists.map(a => a.artistId);
export const mapArtistProfileFilterToInstruction = (filter: ArtistProfileFilterType): server.dto.IsArtistTypeFilterOptions => filter.option.value;
export const mapBookkeepingFilterToInstruction = (filter: BookkeepingFilterType): number[] => filter.selectedBookkeepings.map(b => b.bookkeepingId);
export const mapSingleBookkeepingFilterToInstruction = (filter: BookkeepingFilterType): number | undefined => filter.selectedBookkeepings.length > 0 ? filter.selectedBookkeepings[0].bookkeepingId : undefined;
export const mapClientFilterToInstruction: (filter: ClientFilterType) => number[] = (filter) => filter.clients.map(c => c.clientId);
export const mapClientAccessFilterToInstruction: (filter: ClientAccessFilterType) => server.dto.ClientAccesses[] = (filter) => filter.selectedClientAccesses.map(c => c.value);
export const mapCompanyFilterToInstruction = (filter: CompanyFilterType): number[] => filter.selectedCompanies.map(c => c.companyId);
export const mapContactFilterToInstruction = (filter: ContactFilterType): number[] => filter.selectedContacts.map(c => c.contactId);
export const mapCostCenterFilterToInstruction = (filter: CostCenterFilterType): number[] => filter.selectedCostCenters.map(c => c.costCenterId);
export const mapCountryFilterToInstruction = (filter: CountryFilterType): server.dto.Countries[] => filter.selectedCountries.map(c => c.key);
export const mapCultureFilterToInstruction = (filter: CultureFilterType): server.dto.LCID[] => filter.selectedLCIDs;
export const mapDataClearedFilterToInstruction = (filter: DataClearedFilterType): server.dto.DataClearedFilterSettings => filter.dataCleared;
export const mapEnvironmentFilterToInstruction = (filter: EnvironmentFilterType): server.dto.Environments[] => filter.options.map(o => o.value);
export const mapExtractorSessionLifeCycleFilterToInstruction = (filter: ExtractorSessionLifeCycleFilterType): server.dto.ExtractorSessionLifeCycles[] => filter.selectedLifeCycles;
export const mapDocumentProgressFilterToInstruction = (filter: DocumentProgressFilterType): server.dto.DocumentLifeCycleFilterOptions => filter.option.value;
export const mapFavoriteArtistFilterToInstruction = (filter: FavoriteArtistFilterType): boolean => filter.onlyFavorite;
export const mapFinancialRiskFilterToInstruction = (filter: FinancialRiskFilterType): server.dto.FinancialRiskLevels[] => filter.selectedRiskLevels;
export const mapFunctionGroupsFilterToInstruction = (filter: FunctionGroupFilterType): server.dto.FunctionGroups[] => filter.selectedFunctionGroups;
export const mapFreefieldProfileFilterToInstruction = (filter: FreefieldProfileFilterType): number[] => filter.freefieldProfiles.map(p => p.freeFieldProfileId);
export const mapGroupByPeriodFilterToInstruction = (filter: GroupByPeriodFilterType): server.dto.PeriodOptions => filter.groupBy;
export const mapGuestlistAccessTypeFilterToInstruction = (filter: GuestlistAccessFilterType): number[] => filter.selectedLists.map(at => at.guestlistAccessTypeId);
export const mapHotelFilterToInstruction = (filter: HotelFilterType): number[] => filter.selectedHotels.map(h => h.companyId);
export const mapHubSpotFilterToInstruction = (filter: HubSpotLinkedFilterType): server.dto.LinkedFilterSettings => filter.linked;
export const mapDataClearedToInstruction = (filter: DataClearedFilterType): server.dto.DataClearedFilterSettings => filter.dataCleared;
export const mapInvoiceProgressFilterToInstruction = (filter: InvoiceProgressFilterType): server.dto.InvoiceLifeCycleFilterOptions => filter.option.value;
export const mapKeywordFilterToInstruction = (filter: KeywordFilterType): string => filter.query;
export const mapMonthFilterToInstruction = (filter: MonthFilterType): Date => filter.month?.format("YYYY-MM-DD") as any;
export const mapNotificationStatusFilterToInstruction = (filter: NotificationStatusFilterType): server.dto.NotificationStatusFilterSettings => filter.selectedNotificationStatus;
export const mapOnlyMySelectionFilterToInstruction = (filter: OnlyMySelectionFilterType): boolean => filter.showOnlyMySelection;
export const mapOwnersFilterToInstruction = (filter: OwnersFilterType): number[] => filter.selectedOwners.map(o => o.userId);
export const mapSingleOwnerFilterToInstruction = (filter: OwnersFilterType): number | undefined => filter.selectedOwners.length > 0 ? filter.selectedOwners[0].userId : undefined;
export const mapQuarterFilterToInstruction = (filter: QuarterFilterType): Date => filter.quarter?.format("YYYY-MM-DD") as any;
export const mapPaymentMethodFilterToInstruction = (filter: PaymentMethodFilterType): server.dto.PaymentMethods[] => filter.selectedPaymentMethods;
export const mapPeriodFilterToInstruction = (filter: PeriodFilterType): server.dto.PeriodFilterInstruction => filter.period === "unknown"
  ? undefined
  : ({
    period: filter.period,
    selectionEnd: filter.selectionEnd?.format("YYYY-MM-DD") as any,
    selectionStart: filter.selectionStart?.format("YYYY-MM-DD") as any,
    selectBySendOrDueDate: filter.target
  });
export const mapProductGroupFilterToInstruction = (filter: ProductGroupFilterType): number[] => filter.selectedProductGroups.map(p => p.productGroupId);
export const mapProductionFilterToInstruction = (filter: ProductionFilterType): number[] => filter.selectedProductions.map(p => p.productionId);
export const mapPromoterFilterToInstruction = (filter: PromoterFilterType): number[] => filter.selectedPromoters.map(p => p.companyId);
export const mapPublicationFilterToInstruction = (filter: PublicationFilterType): server.dto.PublicationStatuses => filter.selectedPublication;
export const mapStatusFilterToInstruction = (filter: StatusFilterType): server.dto.StatusFilterInstruction => ({
  statusIds: filter.selectedStatuses.map(s => s.statusId),
  statusesWhereInOrNotIn: filter.filterType
});
export const mapTagFilterToInstruction = (filter: TagFilterType): server.dto.TagFilterInstruction => ({
  tagIds: filter.selectedTags.map((t) => t.tagId),
  tagsWhereInOrNotIn: filter.filterType
});
export const mapTaskManagerPeriodFilterToInstruction = (filter: TaskManagerPeriodFilterType): server.dto.TaskManagerPeriodFilters => filter.selectedTaskManagerPeriod;
export const mapUserToClientLifecycleToInstruction = (filter: UserToClientLifecycleFilterType): server.dto.UserToClientLifeCycles[] => filter.options.map(o => o.value);
export const mapVenueFilterToInstruction = (filter: VenueFilterType): number[] => filter.selectedVenues.map(v => v.venueId);
export const mapVenueCategoryFilterToInstruction = (filter: VenueCategoryFilterType): server.dto.VenueCategories[] => filter.selectedVenueCategories;
export const mapVenueCapacityFilterToInstruction = (filter: VenueCapacityFilterType): server.dto.VenueCapacityFilterInstruction => ({
  min: filter.min,
  max: filter.max
});
export const mapVisibilityFilterToInstruction = (filter: VisibleFilterType): server.dto.BooleanFilterSettings => filter.visibility;
export const mapWeekFilterToInstruction = (filter: WeekFilterType): Date => filter.week?.format("YYYY-MM-DD") as any;

const getOrder = (filter: Partial<FilterValues>) => {
  if (filter.clientFileSort) {
    return filter.clientFileSort.sortOption.value;
  } else if (filter.clientSort) {
    return filter.clientSort.sortOption.value;
  } else if (filter.companySort) {
    return filter.companySort.sortOption.value;
  } else if (filter.contactSort) {
    return filter.contactSort.sortOption.value;
  } else if (filter.showSort) {
    return filter.showSort.sortOption.value;
  } else if (filter.invoiceSort) {
    return filter.invoiceSort.sortOption.value;
  } else if (filter.documentSort) {
    return filter.documentSort.sortOption.value;
  } else if (filter.paymentSort) {
    return filter.paymentSort.sortOption.value;
  } else if (filter.productionSort) {
    return filter.productionSort.sortOption.value;
  } else if (filter.plannerSort) {
    return filter.plannerSort.sortOption.value;
  } else if (filter.taskManagerSort) {
    return filter.taskManagerSort.sortOption.value;
  }
  return null;
};

const getLifeCycle = (filter: Partial<FilterValues>) => {
  if (filter.documentProgress) {
    return mapDocumentProgressFilterToInstruction(filter.documentProgress);
  } else if (filter.invoiceProgress) {
    return mapInvoiceProgressFilterToInstruction(filter.invoiceProgress);
  }
  return null;
};

const getDate = (filter: Partial<FilterValues>) => {
  if (filter.week) {
    return mapWeekFilterToInstruction(filter.week);
  } else if (filter.month) {
    return mapMonthFilterToInstruction(filter.month);
  } else if (filter.quarter) {
    return mapQuarterFilterToInstruction(filter.quarter);
  }
  return null;
}

export const appendFilterValuesToQueryString = (filter: Partial<FilterValues>): void => {
  const obj = {
    address: filter.address ? mapAddressFilterToInstruction(filter.address) : null,
    airportSize: filter.airportSize ? mapAirportSizeFilterToInstruction(filter.airportSize) : null,
    phases: filter.appointmentPhase ? mapAppointPhaseFilterToInstruction(filter.appointmentPhase) : null,
    appointmentTypes: filter.appointmentType ? mapAppointmentTypeFilterToInstruction(filter.appointmentType) : null,
    artistIds: filter.artist ? mapArtistFilterToInstruction(filter.artist) : null,
    profile: filter.artistProfile ? mapArtistProfileFilterToInstruction(filter.artistProfile) : null,
    bookkeepingIds: filter.bookkeepings ? mapBookkeepingFilterToInstruction(filter.bookkeepings) : null,
    clientIds: filter.client ? mapClientFilterToInstruction(filter.client) : null,
    clientAccesses: filter.clientAccess ? mapClientAccessFilterToInstruction(filter.clientAccess) : null,
    companyIds: filter.company ? mapCompanyFilterToInstruction(filter.company) : null,
    contactIds: filter.contact ? mapContactFilterToInstruction(filter.contact) : null,
    costCenterIds: filter.costCenter ? mapCostCenterFilterToInstruction(filter.costCenter) : null,
    cultures: filter.culture ? mapCultureFilterToInstruction(filter.culture) : null,
    countryIds: filter.country ? mapCountryFilterToInstruction(filter.country) : null,
    date: getDate(filter),
    dataCleared: filter.dataCleared ? mapDataClearedFilterToInstruction(filter.dataCleared) : null,
    environments: filter.environment ? mapEnvironmentFilterToInstruction(filter.environment) : null,
    lifeCycles: filter.extractorSessionLifeCycle ? mapExtractorSessionLifeCycleFilterToInstruction(filter.extractorSessionLifeCycle) : null,
    lifeCycle: getLifeCycle(filter),
    onlyFavorite: filter.favoriteArtists ? mapFavoriteArtistFilterToInstruction(filter.favoriteArtists) : null,
    financialRisks: filter.financialRisks ? mapFinancialRiskFilterToInstruction(filter.financialRisks) : null,
    functionGroups: filter.functionGroups ? mapFunctionGroupsFilterToInstruction(filter.functionGroups) : null,
    freeFieldProfileIds: filter.freefieldProfile ? mapFreefieldProfileFilterToInstruction(filter.freefieldProfile) : null,
    hotelIds: filter.hotel ? mapHotelFilterToInstruction(filter.hotel) : null,
    keyword: filter.keyword ? mapKeywordFilterToInstruction(filter.keyword) : null,
    linkedToHubspot: filter.hubSpotLinked ? mapHubSpotFilterToInstruction(filter.hubSpotLinked) : null,
    notificationStatus: filter.notificationStatus ? mapNotificationStatusFilterToInstruction(filter.notificationStatus) : null,
    ownerUserIds: filter.owners ? mapOwnersFilterToInstruction(filter.owners) : null,
    paymentMethods: filter.paymentMethod ? mapPaymentMethodFilterToInstruction(filter.paymentMethod) : null,
    period: filter.period ? mapPeriodFilterToInstruction(filter.period) : null,
    productionIds: filter.production ? mapProductionFilterToInstruction(filter.production) : null,
    promoterIds: filter.promoter ? mapPromoterFilterToInstruction(filter.promoter) : null,
    publication: filter.publication ? mapPublicationFilterToInstruction(filter.publication) : null,
    statuses: filter.statuses ? mapStatusFilterToInstruction(filter.statuses) : null,
    tags: filter.tags ? mapTagFilterToInstruction(filter.tags) : null,
    taskManagerPeriod: (filter.taskManagerPeriod && filter.taskManagerPeriod.selectedTaskManagerPeriod !== DefaultTaskManagerPeriodFilter) ? mapTaskManagerPeriodFilterToInstruction(filter.taskManagerPeriod) : null,
    userToClientLifecycle: filter.userToClientLifecycle ? mapUserToClientLifecycleToInstruction(filter.userToClientLifecycle) : null,
    userToClientLifecycles: filter.userToClientLifecycle ? mapUserToClientLifecycleToInstruction(filter.userToClientLifecycle) : null,
    venueIds: filter.venue ? mapVenueFilterToInstruction(filter.venue) : null,
    venueCategoryIds: filter.venueCategory ? mapVenueCategoryFilterToInstruction(filter.venueCategory) : null,
    venueCapacity: filter.venueCapacity ? mapVenueCapacityFilterToInstruction(filter.venueCapacity) : null,
    order: getOrder(filter),
    visible: filter.visible ? mapVisibilityFilterToInstruction(filter.visible) : null,
  };

  appendQueryString(obj);
};
